import { EllipsisVertical } from "lucide-react"
import { createPortal } from "react-dom"
import { useRef, type ReactNode, type RefObject } from "react"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { useDropdownMenu } from "@/components/ui/dropdown-menu-position"
import { cn } from "@/lib/utils"

export type EditToolbarMoreItem = {
  key: string
  label: string
  icon: ReactNode
  onClick: () => void
  disabled?: boolean
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  active: boolean
  menuAria: string
  items: EditToolbarMoreItem[]
  moreButtonRef: RefObject<HTMLButtonElement | null>
  /** Extra block at the top of the menu (e.g. frame format / platform dims). */
  header?: ReactNode
  /**
   * Portal to document.body (viewport coords). Use when a body-level overlay
   * (e.g. 3D scene stage) sits above the dialog stacking context.
   */
  portalToBody?: boolean
  /** "tooltip" uses ActionHintTooltip on the overflow button and omits its native title. */
  hint?: "tooltip"
  menuClassName?: string
  children?: ReactNode
}

const MORE_BTN_CLASS = cn(
  "inline-flex h-8 w-8 items-center justify-center rounded-md text-ui-text-secondary outline-none",
  "hover:bg-ui-surface-raised hover:text-ui-text-primary disabled:pointer-events-none disabled:opacity-40",
  "focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)]",
)

export function EditToolbarMore({
  open,
  onOpenChange,
  active,
  menuAria,
  items,
  moreButtonRef,
  header,
  portalToBody = false,
  hint,
  menuClassName,
  children,
}: Props) {
  const rootRef = useRef<HTMLDivElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const hasMenuBody = items.length > 0 || Boolean(header)
  const { menuStyle, portalRoot } = useDropdownMenu({
    open: open && hasMenuBody,
    direction: "down",
    align: "right",
    anchorRef: moreButtonRef,
    menuRef,
    rootRef,
    positionKey: `${items.map((item) => item.key).join(",")}${header ? ":header" : ""}`,
    estimatedMenuWidthPx: 220,
    resolveWidth: () => ({
      minWidth: "12rem",
      width: "max-content",
      maxWidth: "min(24rem, calc(100vw - 16px))",
    }),
    onOutsideClick: () => onOpenChange(false),
    portalToBody,
  })

  const moreButton = (
    <button
      ref={moreButtonRef}
      type="button"
      title={hint === "tooltip" ? undefined : menuAria}
      aria-label={menuAria}
      aria-expanded={open}
      aria-haspopup="menu"
      className={cn(
        MORE_BTN_CLASS,
        active
          ? "bg-ui-accent/15 text-ui-text-primary ring-1 ring-ui-accent ring-inset"
          : "ring-1 ring-transparent ring-inset",
      )}
      onClick={() => onOpenChange(!open)}
    >
      <EllipsisVertical className="h-4 w-4" />
    </button>
  )

  return (
    <div ref={rootRef} className="relative">
      {hint === "tooltip" ? <ActionHintTooltip label={menuAria}>{moreButton}</ActionHintTooltip> : moreButton}
      {open && hasMenuBody && portalRoot
        ? createPortal(
            <div
              ref={menuRef}
              role="menu"
              style={menuStyle ?? undefined}
              className={cn(
                "overflow-hidden rounded-lg border border-ui-border bg-ui-bg-elevated py-1 shadow-xl shadow-black/40",
                menuClassName,
              )}
            >
              {header ? <div className="px-2 py-1.5">{header}</div> : null}
              {header && items.length > 0 ? <div className="mx-2 h-px bg-ui-border" /> : null}
              {items.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  role="menuitem"
                  title={item.label}
                  disabled={item.disabled}
                  className={cn(
                    "flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-ui-text-primary outline-none",
                    "hover:bg-ui-surface-sunken focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50",
                  )}
                  onClick={() => {
                    onOpenChange(false)
                    item.onClick()
                  }}
                >
                  {item.icon}
                  {item.label}
                </button>
              ))}
            </div>,
            portalRoot,
          )
        : null}
      {children}
    </div>
  )
}
